import { isTruthViolation } from './qaSeverityPolicy';
import { validateSemanticFactBinding } from './semanticFactBinding';
/**
 * Stamped-hold re-evaluation (2026-08-13, Redhead RCA).
 *
 * `qualityHoldFindings` are strings stamped onto a report AT GENERATION by
 * the truth gate of that moment. When the gate's rules improve, the stamps
 * do not — the live Redhead feasibility banner showed "4 findings" of which
 * three were sentences the current absence gate explicitly exempts and one
 * was a stated-precision rounding ("3 km" vs governed 2.9) the grounding
 * gate now accepts, plus a duplicate.
 *
 * The report REVISION is immutable — the hold itself clears only via
 * regeneration, by design. What must not stay frozen is the STORY the
 * banner tells: findings that today's rules would not produce are shown as
 * superseded, not as live defects. This module re-runs the PURE (regex,
 * no-LLM) rule layer over the stamped strings so every hold surface can
 * present current truth.
 */
import type { SiteRagReport } from '../../types/siteRag';
import {
  classifySentenceForTest,
  isScanGroundedSentimentAbsence,
} from './absenceAssertionDetector';
import { numericPartOf, roundsToStatedPrecision } from '../grounding/statedPrecision';
import { isConditionalFutureFraming, isProvenanceQualified } from './caveatHonestyVerifier';

const CAVEAT_STAMP_RE = /^caveatHonesty:.*[\s("]Claim:\s*"([^"]+)"/i;

const ABSENCE_STAMP_RE = /absenceAssertionAudit: Asserted absence without evidence: "([^"]+)"/;
const GROUNDING_STAMP_RE = /grounding: Narrative states "([^"]+)" for [\w.]+; the only permitted value is ([\d,]+(?:\.\d+)?)/;

export interface ReevaluatedHoldFindings {
  /** Stamped findings today's rules still produce — the live defects. */
  findings: string[];
  /**
   * Stamped findings TODAY'S RULES EXEMPT — genuinely superseded because a
   * quality rule changed (an absence exemption now applies, a figure rounds
   * to its stated precision). Duplicates are NOT counted here.
   */
  supersededCount: number;
  /**
   * Byte-identical repeats of a message already kept — the same sentence
   * stamped from several sections. These say nothing new, but nothing about
   * the rules changed either, so they must never be reported as superseded.
   */
  duplicateCount: number;
}

/**
 * Re-evaluate stamped hold findings against the CURRENT pure rule layer.
 * Conservative: only findings whose stamp text parseably matches a rule we
 * can re-run are eligible to be superseded; everything else is kept as-is.
 */
export function reevaluateStampedHoldFindings(
  report: Pick<SiteRagReport, 'reportType'> & { publicSentimentMeta?: unknown; qaFindings?: unknown },
  stamped: readonly string[],
): ReevaluatedHoldFindings {
  const qa = (report.qaFindings && typeof report.qaFindings === 'object'
    ? report.qaFindings
    : undefined) as Record<string, unknown> | undefined;
  const seen = new Set<string>();
  const findings: string[] = [];
  let supersededCount = 0;
  // DUPLICATES ARE NOT RULE CHANGES (2026-08-14). Both were counted into
  // supersededCount and rendered as "N stamped findings are superseded by
  // updated quality rules" — on Triple Threat that told the operator 15
  // findings had been cleared by better rules when in fact ZERO rules
  // changed: all 15 were byte-identical repeats of the 5 surviving
  // messages (one wrong number restated across several sections).
  let duplicateCount = 0;

  for (const raw of stamped) {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (text.length === 0) continue;
    // Duplicate stamps (the same sentence serialized via two sections) tell
    // the user nothing twice.
    if (seen.has(text)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(text);

    const absence = text.match(ABSENCE_STAMP_RE);
    if (absence) {
      const sentence = absence[1]!;
      // The pure classifier returns null when an exemption applies (scope
      // boundary, attributed hedge, requirement framing) — or when the
      // stamp's 120-char truncation clipped the trigger; either way the
      // current gate would not stamp this sentence.
      const stillCaught = classifySentenceForTest(sentence) !== null
        && !isScanGroundedSentimentAbsence(report as SiteRagReport, sentence);
      if (!stillCaught) {
        supersededCount += 1;
        continue;
      }
    }

    const grounding = text.match(GROUNDING_STAMP_RE);
    if (grounding) {
      const numeric = numericPartOf(grounding[1]!);
      const governed = Number(grounding[2]!.replace(/,/g, ''));
      if (numeric !== null && roundsToStatedPrecision(numeric, governed)) {
        supersededCount += 1;
        continue;
      }
    }

    const caveat = text.match(CAVEAT_STAMP_RE);
    if (caveat) {
      const claimExcerpt = caveat[1]!;
      if (isProvenanceQualified(claimExcerpt) || isConditionalFutureFraming(claimExcerpt)) {
        supersededCount += 1;
        continue;
      }
    }

    if (qa) {
      const colonIdx = text.indexOf(': ');
      if (colonIdx > 0) {
        const detector = text.slice(0, colonIdx).trim();
        const msgBody = text.slice(colonIdx + 2).trim();
        if (detector === 'sourceRoleTruth' || detector === 'internalNumberFindings' || detector === 'waterUseScreening') {
          const bucket = qa[detector];
          const rows: Record<string, unknown>[] = Array.isArray(bucket)
            ? (bucket as Record<string, unknown>[])
            : Array.isArray((bucket as { findings?: unknown[] } | undefined)?.findings)
              ? ((bucket as { findings: Record<string, unknown>[] }).findings)
              : Array.isArray((bucket as { violations?: unknown[] } | undefined)?.violations)
                ? ((bucket as { violations: Record<string, unknown>[] }).violations)
                : [];
          const matching = rows.filter((r) => {
            const m = String(r.message ?? r.detail ?? r.kind ?? r.label ?? '').trim();
            return m === msgBody;
          });
          if (matching.length > 0 && matching.every((r) => !isTruthViolation(detector, r))) {
            supersededCount += 1;
            continue;
          }
        } else if (detector === 'grounding') {
          const gBucket = qa.grounding as { violations?: Record<string, unknown>[]; blocking?: Record<string, unknown>[] } | undefined;
          const rows = [...(gBucket?.violations ?? []), ...(gBucket?.blocking ?? [])];
          const matching = rows.filter((r) => String(r.message ?? '').trim() === msgBody);
          if (
            matching.length > 0 &&
            matching.every(
              (r) =>
                !isTruthViolation('grounding', r) ||
                (typeof r.factId === 'string' &&
                  !validateSemanticFactBinding({
                    factId: r.factId,
                    claimText: String(r.sentenceText ?? r.claimText ?? r.excerpt ?? ''),
                    observedText: typeof r.observed === 'string' ? r.observed : undefined,
                    expectedText: typeof r.expected === 'string' ? r.expected : undefined,
                    message: String(r.message ?? ''),
                  }).ok),
            )
          ) {
            supersededCount += 1;
            continue;
          }
        }
      }
    }

    findings.push(text);
  }

  return { findings, supersededCount, duplicateCount };
}
